import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { GripVertical, Loader2, Trash2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel
} from "@/components/ui/form"
import { Dropdown } from '../ui/filterSelect';
import { DatePickerInput } from '../ui/date';
import { OptionSet } from "@/components/ui/optionset";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { useAppHub } from "@/realtime/AppHubProvider";

import { Popup } from "../common/Popup";
import { useApiFetch } from "@/helper/apiFetch";
import { useTranslate } from "@/helper/translate";
import { ExperienceSchema, PlenumFrameSchema, PlenumInfoSchema, PlenumSchema, SurveyInfoSchema } from "./PlenumSchema";
import { TagBox } from "../ui/tagbox";
import { Input } from "../ui/input";
import { Toggle } from "../ui/toggle";
import { Checkbox } from "../ui/checkbox";
import { NumberInput } from "../ui/number";
import { PresentationInfo } from "./PresentationInfo";
import { SurveyInfo } from "./SurveyInfo";
import { API_BASE_URL } from "@/config";
type PlenumInfoProps = {
  plenumId: number | null;
  eventId: string;
  onClose: (plenum?: z.infer<typeof PlenumSchema>) => void;
  onDeleted: () => void;
};

type Lookup = {

  Id: number;
  Value: string;
  Name: string;
  Description: string;
  id?: string | number;

  description?: string;
  EmojiTypeId?: string | number;
  emojiTypeId?: string | number;
};
type Bridge = { conferenceID: string, name: string };
type FrameSource = { id?: string; Id?: string; name?: string; Name?: string; connTypeId?: number; ConTypeId?: number };
type Presentation = { Id: number;   Intro: boolean; Position: number; Title: string; EventId: string; PlenumId: number; Type: string; PointerEvents: boolean;  };


type ConnType = { ConnTypeId: number, type_name: string };
const tabs = [
  { key: "features", label: "Common Features" },
  { key: "language", label: "Language Features" },
  { key: "frames", label: "Stream Sources" },
  { key: "qa", label: "Q & A" },
  { key: "presentations", label: "Presentations" },
  { key: "surveys", label: "Surveys" },
];


const joinType = [
  { key: 0, label: "Default: When Talkrequest is clicked" },
  { key: 1, label: "BEFORE entering Plenum (not compatible with Lobby Type 'Agenda'" },
  { key: 2, label: "AFTER entering Plenum" }
];


export function PlenumInfo({ plenumId, eventId, onClose, onDeleted }: PlenumInfoProps) {

  const apiFetch = useApiFetch();
  const { translate } = useTranslate();
  const [errorText, setError] = useState("");
  const [isDelete, prepareDelete] = useState(false);
  const [languages, setLanguages] = useState<Lookup[]>([]);
  const [chats, setChats] = useState<Lookup[]>([]);
  const [emojis, setEmojis] = useState<Lookup[]>([]);
  const [bridges, setBridges] = useState<Bridge[]>([]);
  const [connTypes, setConnTypes] = useState<ConnType[]>([]);
  const [frameSources, setFrameSources] = useState<FrameSource[]>([]);
  const [presentations, setPresentations] = useState<Presentation[]>([]);
  const [presentationId, setPresentationId] = useState<number | null>(null);
  const [surveys, setSurveys] = useState<SurveyInfoSchema[]>([]);
  const [surveyId, setSurveyId] = useState<number | undefined>(undefined);
  const [showSurveyReset, setShowSurveyReset] = useState<number | null>(null);
  const [draggedPresentationIndex, setDraggedPresentationIndex] = useState<number | null>(null);
  const [dragOverPresentationIndex, setDragOverPresentationIndex] = useState<number | null>(null);
  const [tabIndex, setIndex] = useState(0);
  const [languageTabIndex, setLanguageTabIndex] = useState(0);
  const [selectedFrameLanguageId, setSelectedFrameLanguage] = useState(0);
  const [frameContentMode, setFrameContentMode] = useState<"primary" | "secondary">("primary");
  const [isSaving, setIsSaving] = useState(false);
  const [currentPlenumId, setCurrentPlenumId] = useState<number>(plenumId ?? -1);
  const { connection, isConnected: isHubConnected } = useAppHub();

  const form = useForm<z.infer<typeof ExperienceSchema>>({
    resolver: zodResolver(ExperienceSchema), defaultValues: {

    }
  });



  type Option = {
    label: string;
    value: number | boolean;
  };



  const serverTypes: Option[] = [
    { value: false, label: "Unreal Server" },
    { value: true, label: "Jitsi Server" },
  ];





  const plenumLanguages = form.watch("PlenumLanguages") ?? [];
  const plenumFrames = form.watch("PlenumFrames") ?? [];

  useEffect(() => {
    setCurrentPlenumId(plenumId ?? -1);
  }, [plenumId]);








  const isPrimaryFrame = frameContentMode === "primary";

  const activeFrameIndex = plenumFrames.findIndex(
    (x) => x.PlenumLanguageId === selectedFrameLanguageId && !!x.IsPrimaryContent === isPrimaryFrame
  );

  const activeFrameBasePath = activeFrameIndex >= 0 ? `PlenumFrames.${activeFrameIndex}` : null;



  const getFilteredFrameSources = (connTypeValue: unknown) => {
    const numericConnType = Number(connTypeValue ?? 0);
    return frameSources.filter((item) => Number(item.connTypeId ?? item.ConTypeId ?? 0) === numericConnType);
  };


  const DeletePresentation = async (id?: number) => {
    if (!id) return;
   const res = await apiFetch(`/api/eventpool/DeleteEventPresentation?key=${id}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const e = await res.text();
      setError(e);
    }
    else {
      setPresentations(presentations.filter(p => p.Id !== id));
    }
  }
  const reseedPresentationPositions = (items: Presentation[]) => {
    const sorted = [...items].sort((a, b) => Number(a.Position ?? 0) - Number(b.Position ?? 0));
    const positions = sorted.map((x) => Number(x.Position ?? 0));
    const hasValidOrder = positions.every((p, idx) => p === idx + 1);

    if (hasValidOrder) return sorted;

    return sorted.map((item, index) => ({
      ...item,
      Position: index + 1,
    }));
  };

  const movePresentationItem = (list: Presentation[], fromIndex: number, toIndex: number) => {
    const updated = [...list];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    return updated.map((item, index) => ({
      ...item,
      Position: index + 1,
    }));
  };

  const handlePresentationDrop = (targetIndex: number) => {
    if (draggedPresentationIndex == null) return;
    if (draggedPresentationIndex === targetIndex) return;

    const reseeded = reseedPresentationPositions(presentations);
    const newList = movePresentationItem(reseeded, draggedPresentationIndex, targetIndex);

    //api
      apiFetch(`/admin/PutPresentationPositions`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newList.map((x) => ({ Id: x.Id, Position: x.Position }))),
      }).then((res) => {
        if (!res.ok) {
          // Handle error
        }
        else
          setPresentations(newList);


      });

  };

  const normalizeTagBoxValue = (rawValue: unknown): string => {
    if (rawValue === null || rawValue === undefined) return "";

    if (Array.isArray(rawValue)) {
      return rawValue
        .map((x) => String(x ?? "").trim())
        .filter((x) => x.length > 0)
        .join(",");
    }

    const value = String(rawValue).trim();
    if (!value) return "";

    // Support values coming from DB as JSON arrays or semicolon-separated lists.
    if (value.startsWith("[") && value.endsWith("]")) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed
            .map((x) => String(x ?? "").trim())
            .filter((x) => x.length > 0)
            .join(",");
        }
      } catch {
        // Fall back to delimiter-based normalization.
      }
    }

    return value
      .split(/[;,]/)
      .map((x) => x.trim())
      .filter((x) => x.length > 0)
      .join(",");
  };

  const normalizeEmojiLookup = (raw: unknown): Lookup[] => {
    const items = Array.isArray(raw) ? raw : [];

    return items.map((item) => {
      const record = (item ?? {}) as Record<string, unknown>;
      const id = record.Id ?? record.ID ?? record.id ?? record.EmojiTypeId ?? record.EmojiTypeID ?? record.emojiTypeId ?? "";
      const description = record.Description ?? record.description ?? record.Name ?? record.name ?? record.Value ?? "";

      return {
        conferenceID: String(record.conferenceID ?? ""),
        Id: Number(id),
        Value: String(record.Value ?? description ?? ""),
        Name: String(record.Name ?? record.name ?? description ?? ""),
        Description: String(description),
        id: id as string | number,
        name: String(record.name ?? record.Name ?? ""),
        description: String(record.description ?? record.Description ?? ""),
        EmojiTypeId: (record.EmojiTypeId ?? record.EmojiTypeID ?? record.emojiTypeId ?? id) as string | number,
        emojiTypeId: (record.emojiTypeId ?? record.EmojiTypeId ?? record.EmojiTypeID ?? id) as string | number,
      };
    });
  };
  async function deletePlenum() {


    const res = await apiFetch(`/api/eventpool/DeletePlenum?key=${currentPlenumId}`, {
      method: "DELETE",
    });

    if (!res.ok) {
      const e = await res.text();
      setError(e);

    }
    else {

      onDeleted();
    }


  }
  async function deleteSurveyAnswers(id?: number) {
    if (!id) return;


    const res = await apiFetch(`/addons/ResetSurveyResults?surveyId=${id}&plenumId=${currentPlenumId}`, {
      method: "POST",
    });

    if (!res.ok) {
      const e = await res.text();
      setError(e);

    }
  

  }

  const Save = async (values: z.infer<typeof ExperienceSchema>): Promise<boolean> => {

    try {
      setError("");



      const fd = new FormData();

      const appendFormData = (formData: FormData, key: string, value: unknown) => {
        if (value === null || value === undefined) {
          formData.append(key, "");
          return;
        }

        if (value instanceof Date) {
          formData.append(key, value.toISOString());
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            appendFormData(formData, `${key}[${index}]`, item);
          });
          return;
        }

        if (typeof value === "object") {
          Object.entries(value as Record<string, unknown>).forEach(([childKey, childValue]) => {
            appendFormData(formData, `${key}[${childKey}]`, childValue);
          });
          return;
        }

        formData.append(key, String(value));
      };

      Object.entries(values).forEach(([rootKey, rootValue]) => {
        appendFormData(fd, rootKey, rootValue);
      });

      const res = await apiFetch(`/Admin/SavePlenum`, { method: "POST", body: fd });



      if (!res.ok) {
        const e = await res.text();
        setError(translate("ui.failedsave") + e);
        return false;
      }
      else {

        if (currentPlenumId <= 0) {
          const newPlenumId = Number(await res.text());
          setCurrentPlenumId(newPlenumId);
          form.setValue("Plenum.Id", newPlenumId);
          await loadPlenumInfo(newPlenumId);
          return true;
        }
        await loadPlenumInfo();
        return true;



      }
    } catch (err) {
      console.error(err);
      return false;
    }
  }


     const loadPresentations = async () => {
        const res = await apiFetch(`/api/EventPool/GetEventPresentations?eventId=${eventId}`);
        if (!res.ok) {
          return;
        }

        const data = await res.json();
        setPresentations(reseedPresentationPositions(Array.isArray(data) ? data.filter(s=>s.PlenumId === currentPlenumId) : []));
      }

      
  const DeleteSurvey = async (id?: number) => {
    if (!id) return;
   const res = await apiFetch(`/api/eventpool/DeleteSurveyDesign?key=${id}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const e = await res.text();
      setError(e);
    }
    else {
      setSurveys(surveys.filter(p => p.Id !== id));
    }
  }


       const loadSurveys = async () => {
      try {

        
        const res = await apiFetch(`/api/EventPool/GetSurveyDesignListByEvent?eventId=${eventId}`);
        if (!res.ok) {
          return;
        }

        const data = await res.json();
        setSurveys(Array.isArray(data) ? data.filter(s=>s.PlenumId === currentPlenumId) : []);

       

      } catch (err) {
        console.error(err);
        
      } 
    };

    const toggleSurveyLive = async (surveyId: number | null) => {
    if(surveyId == null) return;

     if (!connection || !isHubConnected) 
      throw new Error("Hub is not connected");
    

    const current = surveys.find((item) => item.Id === surveyId);
    const nextLive = !(current?.Live ?? false);

    try {

      await connection.invoke("OnAdminActivateSurvey", surveyId, nextLive);

        setSurveys((prev) => prev.map((item) =>
          item.Id === surveyId ? { ...item, Live: nextLive } : item
        ));

      }
     catch (err) {
      console.error(err);
     
      setError("Failed to update Live state");
    }


  }


  const onSubmit = async (values: z.infer<typeof ExperienceSchema>) => {

    var success = await Save(values);
    if (success)
      onClose(values.Plenum);
  }

  const loadPlenumInfo = async (idOverride?: number) => {
    try {
      const id = idOverride ?? currentPlenumId;
      const res = await apiFetch(`/Admin/GetPlenum?plenumId=${id > 0 ? id : ""}&eventId=${eventId ?? ""}`);
      if (!res.ok) {
        const e = await res.text();
        setError(e);
      }
      else {
        const plenumJson = await res.json();
        //translate join type
        if (plenumJson.Plenum.PredefineTRType == true)
          plenumJson.Plenum.JoinType = 1;
        else if (plenumJson.Plenum.ShowDialogOnJoin == true)
          plenumJson.Plenum.JoinType = 2;
        else
          plenumJson.Plenum.JoinType = 0;
        form.reset(plenumJson);
      }
    } catch (err) {
      console.error(err);
    }

  }
  useEffect(() => {

    let active = true;

    const load = async () => {
      try {
        const [plenumRes, /*networkRes,*/ chatRes, emojiRes, languagesRes] = await Promise.all([
          loadPlenumInfo(),
          // apiFetch(`/api/EventPool/GetNetworkAreaLookup?eventId=${eventId ?? ""}`),
          apiFetch(`/api/EventPool/GetChatRoomsLookup?eventId=${eventId ?? ""}`),
          apiFetch(`/api/EventPool/GetEmojiTypes`),
          apiFetch(`/api/EventPool/GetAllLanguagesFull`),
        ]);

        if (!active) return;


        // if (networkRes.ok) {
        //   const typesJson = await networkRes.json();
        //   if (active) setNetworkAreas(typesJson.data);
        // }

        if (chatRes.ok) {
          const chatsJson = await chatRes.json();
          if (active) setChats(chatsJson.data);
        }

        if (emojiRes.ok) {
          const emojiJson = await emojiRes.json();
          if (active) {
            setEmojis(normalizeEmojiLookup(emojiJson?.data ?? emojiJson));
          }
        }

        if (languagesRes.ok) {
          const languagesJson = await languagesRes.json();
          if (active) setLanguages(languagesJson.data);
        }



      } catch (err) {
        console.error(err);
        //setLoading(false);
      }
    };
    load();

    return () => {
      active = false;
    };

  }, []);

  useEffect(() => {

    if (tabIndex == 1) {

      //check if we need to save
      if (currentPlenumId <= 0 || !plenumLanguages || plenumLanguages.length === 0 || plenumLanguages.some((l) => !(form.getValues("Plenum.Languages")?.split(",").includes(l.Language ?? ""))))
        Save(form.getValues());




      const loadBridges = async () => {
        const res = await apiFetch(`/api/EventPool/GetAllTurboBridges`);
        if (!res.ok) {
          return;
        }

        const data = await res.json();
        setBridges(data);
      }


      if (!bridges || bridges.length == 0)
        loadBridges();


    }
    else if (tabIndex == 2) {




      const loadTypes = async () => {
        const res = await apiFetch(`/api/EventPool/GetSectionConnTypes?inframe=true`);
        if (!res.ok) {
          return;
        }

        const data = await res.json();
        setConnTypes(data.data);
      }

      const loadFrameSources = async () => {
        const res = await apiFetch(`/api/EventPool/GetPlenumFrameSources?plenumId=${currentPlenumId}`);
        if (!res.ok) {
          return;
        }

        const data = await res.json();
        setFrameSources(data.data);
      }



      if (!connTypes || connTypes.length == 0)
        loadTypes();
      if (!frameSources || frameSources.length == 0)
        loadFrameSources();

      if (currentPlenumId <= 0 || !plenumLanguages || plenumLanguages.length === 0 || plenumLanguages.some((l) => !(form.getValues("Plenum.Languages")?.split(",").includes(l.Language ?? ""))))
        Save(form.getValues()).then((success) => {
          if (success)
            setSelectedFrameLanguage(form.getValues("PlenumLanguages")?.[0]?.Id ?? 0);
        });

      else
        setSelectedFrameLanguage(form.getValues("PlenumLanguages")?.[0]?.Id ?? 0);


    }

    else if (tabIndex == 3) {




      const loadTypes = async () => {
        const res = await apiFetch(`/admin/LoadQASetup?eventId=${eventId}&plenumId=${currentPlenumId}&newCode=true`);
        if (!res.ok) {
          return;
        }

        const data = await res.json();
        form.reset({
          ...form.getValues(),
          QandA: data,
        });
      }

      if (form.getValues("QandA") == null || form.getValues("QandA")?.EventId == null)
        loadTypes();





    }
    else if (tabIndex == 4) {




   



      if (currentPlenumId <= 0 || !plenumLanguages || plenumLanguages.length === 0 || plenumLanguages.some((l) => !(form.getValues("Plenum.Languages")?.split(",").includes(l.Language ?? ""))))
        Save(form.getValues())
      else if (!presentations || presentations.length == 0)
        loadPresentations();


    }
  else if (tabIndex == 5) {




   



      if (currentPlenumId <= 0)
        Save(form.getValues())
      else if (!surveys || surveys.length == 0)
        loadSurveys();


    }



  }, [tabIndex]);


  useEffect(() => {
    const loadFrames = async (lang: number) => {

      if (currentPlenumId > 0)
        apiFetch(`/api/EventPool/GetPlenumFrames?plenumLanguageId=${lang ?? ""}`)
          .then(res => {
            if (!res.ok) throw new Error("Failed to fetch");
            return res.json();
          })
          .then(data => {
            const frames: z.infer<typeof PlenumFrameSchema>[] = data ?? [];
            //append to existing formdata
            form.setValue("PlenumFrames", [...plenumFrames, ...frames] as z.infer<typeof ExperienceSchema>["PlenumFrames"], {
              shouldDirty: false,
              shouldTouch: false,
              shouldValidate: false,
            });

          }).catch(err => {
            console.error(err);
          });

    }
    if (!plenumFrames.some(f => f.PlenumLanguageId === selectedFrameLanguageId))
      loadFrames(selectedFrameLanguageId);

  }, [selectedFrameLanguageId]);
  const getActiveFramePath = (field: string) => {
    if (activeFrameIndex < 0) return null;
    return `PlenumFrames.${activeFrameIndex}.${field}`;
  };

  const getActiveFrameValue = (field: string) => {
    const path = getActiveFramePath(field);

    if (!path) return undefined;
    return (form.getValues as any)(path);
  };

  const setActiveFrameValue = (field: string, value: unknown) => {
    const path = getActiveFramePath(field);
    if (!path) return;
    (form.setValue as any)(path, value, { shouldDirty: true });
  };

  return (
    <div className="p-5">

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, (error) => { console.log("Validation errors:", error); })} className="space-y-2">
          <div className="flex gap-5 items-center">
            <FormField
              control={form.control}
              name="Plenum.Name"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="Plenum.DateFrom"
              render={({ field }) => (
                <FormItem className="w-[9rem]">
                  <FormLabel>Start Date</FormLabel>
                  <FormControl >
                    <DatePickerInput {...field}
                      onChange={(value) => {
                        field.onChange(value);


                      }}

                      showTime={true}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="Plenum.DateTo"
              render={({ field }) => (
                <FormItem className="w-[9rem]">
                  <FormLabel>End Date</FormLabel>
                  <FormControl>
                    <DatePickerInput {...field}
                      setMidnight={true}
                      onChange={(value) => {
                        field.onChange(value);

                      }}
                      showTime={true}

                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Plenum.IsJitsi"
              render={({ field }) => (
                <FormItem className="">
                  <FormLabel>Server Type</FormLabel>
                  <FormControl>
                    <OptionSet
                      options={serverTypes}
                      value={field.value}
                      onChange={field.onChange}
                      className="flex-col"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="Plenum.Languages"
              render={({ field }) => (
                <FormItem className="">
                  <FormLabel>Languages</FormLabel>
                  <FormControl>
                    <TagBox
                      source={languages}
                      value={field.value}
                      getLabel={(x) => x.Value}
                      getValue={(x) => x.Value}
                      onChange={field.onChange}

                    />
                  </FormControl>
                </FormItem>
              )}
            />

          </div>
          <div className="!mt-0">

            <div className="mt-4 flex flex-wrap items-end justify-between gap-4 border-b-2 border-[#ddd]">
              <div className="tabs mt-0 flex-1 !border-b-0">
                {tabs.map((tab, index) => (
                  <span
                    key={index}
                    className={`tab ${index === tabIndex ? 'active' : ''}`}
                    onClick={() => setIndex(index)}
                  >
                    {tab.label}
                  </span>
                ))}
              </div>


            </div>

            <div >






              <div className="" key={tabIndex}>

                {tabIndex == 0 && (
                  <div>
                    <div className='flex gap-y-8 gap-x-4 items-top mt-2'>
                      <div className={!form.watch("Plenum.JoinPassive") ? "rounded-md border border-gray-300 bg-gray-100 p-2" : ""}>
                        <FormField
                          control={form.control}
                          name="Plenum.JoinPassive"
                          render={({ field }) => (
                            <FormItem className={form.watch("Plenum.JoinPassive") ? "pt-6 flex items-center space-x-2" : "flex items-center space-x-2"}  >
                              <FormControl>
                                <Toggle
                                  title="Talk Request"
                                  checked={!field.value}
                                  onChange={(e) => field.onChange(!e.target.checked)}
                                  name={field.name}
                                  onBlur={field.onBlur}
                                  ref={field.ref}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        {!form.watch("Plenum.JoinPassive") && (
                          <div className="mt-3">
                            <FormField
                              control={form.control}
                              name="Plenum.JoinType"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Join Type</FormLabel>
                                  <FormControl>
                                    <Dropdown
                                      {...field}
                                      source={joinType}
                                      getLabel={(x) => x.label}
                                      getValue={(x) => x.key}
                                      searchable={false}
                                      disabled={false}
                                      onChange={(value) => {
                                        if (value == 0) { form.setValue("Plenum.PredefineTRType", false); form.setValue("Plenum.ShowDialogOnJoin", false); }
                                        else if (value == 1) { form.setValue("Plenum.PredefineTRType", true); form.setValue("Plenum.ShowDialogOnJoin", false); }
                                        else if (value == 2) { form.setValue("Plenum.PredefineTRType", false); form.setValue("Plenum.ShowDialogOnJoin", true); }
                                        field.onChange(value);

                                      }}

                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                            <div className="flex items-center space-x-2">





                              <FormField
                                control={form.control}
                                name="Plenum.JoinPerPhone"
                                render={({ field }) => (
                                  <FormItem className="col-span-3">
                                    <FormControl>
                                      <Checkbox
                                        label="Join per Phone"
                                        checked={!!field.value}
                                        onCheckedChange={field.onChange}
                                        name={field.name}
                                        onBlur={field.onBlur}
                                        ref={field.ref}
                                        className="mt-6"
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="Plenum.JoinPerWebAudio"
                                render={({ field }) => (
                                  <FormItem className="col-span-3">
                                    <FormControl>
                                      <Checkbox
                                        label="Join per WebAudio"
                                        checked={!!field.value}
                                        onCheckedChange={field.onChange}
                                        name={field.name}
                                        onBlur={field.onBlur}
                                        ref={field.ref}
                                        className="mt-6"
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="Plenum.JoinPerCamera"
                                render={({ field }) => (
                                  <FormItem className="col-span-3">
                                    <FormControl>
                                      <Checkbox
                                        label="Join per Camera"
                                        checked={!!field.value}
                                        onCheckedChange={field.onChange}
                                        name={field.name}
                                        onBlur={field.onBlur}
                                        ref={field.ref}
                                        className="mt-6"
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </div>


                            {form.watch("Plenum.JoinPerCamera") && (
                              <div className="flex items-center space-x-2 mt-4">
                                <FormField
                                  control={form.control}
                                  name="Plenum.UserLimitTotal"

                                  render={({ field }) => (
                                    <FormItem className="w-[8rem]">
                                      <FormLabel>User Limit Total</FormLabel>
                                      <FormControl>
                                        <NumberInput
                                          value={field.value ?? ""}
                                          onChange={field.onChange}

                                        />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                /> <FormField
                                  control={form.control}
                                  name="Plenum.MatrixLimit"

                                  render={({ field }) => (
                                    <FormItem className="w-[8rem]">
                                      <FormLabel>Matrix Limit</FormLabel>
                                      <FormControl>
                                        <NumberInput
                                          value={field.value ?? ""}
                                          onChange={field.onChange}

                                        />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                /> <FormField
                                  control={form.control}
                                  name="Plenum.VideoBitrate"

                                  render={({ field }) => (
                                    <FormItem className="w-[8rem]">
                                      <FormLabel>Video Bitrate</FormLabel>
                                      <FormControl>
                                        <NumberInput
                                          value={field.value ?? ""}
                                          onChange={field.onChange}

                                        />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />


                              </div>)}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-y-8 gap-x-4 items-top mt-3">
                        <FormField
                          control={form.control}
                          name="Plenum.BackstageTCId"
                          render={({ field }) => (
                            <FormItem className="col-span-2">
                              <FormLabel>Backstage Chat</FormLabel>
                              <FormControl>
                                <Dropdown
                                  {...field}
                                  source={chats}
                                  getLabel={(x) => x.Name}
                                  getValue={(x) => x.Id}
                                  onChange={(value) => {
                                    field.onChange(value); // update orderId

                                  }}
                                />
                              </FormControl>
                            </FormItem>

                          )}
                        />
                        <FormField
                          control={form.control}
                          name="Plenum.EmojiTypes"
                          render={({ field }) => (
                            <FormItem className="">
                              <FormLabel>Emoji Types</FormLabel>
                              <FormControl>
                                <TagBox
                                  source={emojis}
                                  value={normalizeTagBoxValue(field.value)}
                                  getLabel={(x) => String(x.Description ?? x.description ?? x.Name ?? x.Value ?? x.Id ?? x.id ?? x.EmojiTypeId ?? x.emojiTypeId ?? "")}
                                  getValue={(x) => String(x.Id ?? x.id ?? x.EmojiTypeId ?? x.emojiTypeId ?? "")}
                                  onChange={field.onChange}

                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />



                        <FormField
                          control={form.control}
                          name="Plenum.HasPhotoWall"
                          render={({ field }) => (
                            <FormItem className="pt-6">
                              <FormControl>
                                <Toggle
                                  title="Photo Wall"
                                  checked={!!field.value}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                  name={field.name}
                                  onBlur={field.onBlur}
                                  ref={field.ref}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        {/* <FormField
            control={form.control}
            name="Plenum.NetworkingAreaId"
            render={({ field }) => (
              <FormItem className="col-span-3">
                <FormLabel>Networking Area</FormLabel>
                <FormControl>
                  <Dropdown
                    {...field}
                    source={networkAreas}
                    getLabel={(x) => x.Name}
                    getValue={(x) => x.Id}
                    searchable={true}
                  />
                </FormControl>
              </FormItem>
            )}
          /> */}


                      </div>
                    </div>
                  </div>
                )}
                {tabIndex == 1 && (
                  <div>

                    {plenumLanguages.length === 0 ? (
                      <div className="rounded-md border border-gray-300 bg-gray-100 p-3 text-sm text-gray-700">
                        Select one or more values in Languages to configure language-specific settings.
                      </div>
                    ) : (
                      <>
                        <div className="mb-4 flex flex-wrap gap-2 border-b border-gray-300 pb-2">
                          {plenumLanguages.map((language, index) => (
                            <button
                              key={language.Language}
                              type="button"
                              className={`rounded-md border px-3 py-1 text-sm ${index === languageTabIndex ? "!border-blue-500 !bg-blue-50 !text-blue-700" : "border-gray-300 bg-white text-gray-700"}`}
                              onClick={() => setLanguageTabIndex(index)}
                            >
                              {language.Language}
                            </button>
                          ))}
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                          <FormField
                            control={form.control}
                            name={`PlenumLanguages.${languageTabIndex}.TextChatroomId`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Chatroom</FormLabel>
                                <FormControl>
                                  <Dropdown
                                    source={chats}
                                    value={field.value}
                                    getLabel={(x) => x.Name}
                                    getValue={(x) => x.Id}
                                    onChange={(value) => field.onChange(Number(value))}
                                    searchable={true}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`PlenumLanguages.${languageTabIndex}.BridgeId`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Bridge</FormLabel>
                                <FormControl>
                                  <Dropdown
                                    source={bridges}
                                    value={field.value}
                                    getLabel={(x) => x.name}
                                    getValue={(x) => x.conferenceID}
                                    onChange={field.onChange}
                                    searchable={true}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`PlenumLanguages.${languageTabIndex}.Pin`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Bridge Pin</FormLabel>
                                <FormControl>
                                  <Input {...field} value={field.value ?? ""} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <div></div>
                          <FormField
                            control={form.control}
                            name={`PlenumLanguages.${languageTabIndex}.VideoServer`}
                            render={({ field }) => (
                              <FormItem className="col-span-4">
                                <FormLabel>Video Server</FormLabel>
                                <FormControl>
                                  <Input {...field} value={field.value ?? ""} />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`PlenumLanguages.${languageTabIndex}.VODMedia`}
                            render={({ field }) => (
                              <FormItem className="col-span-2">
                                <FormLabel>VOD URL</FormLabel>
                                <FormControl>
                                  <Input {...field} value={field.value ?? ""} />
                                </FormControl>
                              </FormItem>
                            )}
                          />



                          <FormField
                            control={form.control}
                            name={`PlenumLanguages.${languageTabIndex}.VODHasRecordings`}
                            render={({ field }) => (
                              <FormItem className="pt-6">
                                <FormControl>
                                  <Checkbox
                                    label="Use Director Recording for VOD"
                                    checked={!!field.value}
                                    onCheckedChange={field.onChange}
                                    name={field.name}
                                    onBlur={field.onBlur}
                                    ref={field.ref}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          {form.watch(`PlenumLanguages.${languageTabIndex}.VODHasRecordings`) && (
                            <FormField
                              control={form.control}
                              name={`PlenumLanguages.${languageTabIndex}.VODDelay`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Recording Delay in seconds (+/-)</FormLabel>
                                  <FormControl>
                                    <NumberInput
                                      step="0.1"
                                      value={field.value ?? ""}
                                      onChange={(value) => field.onChange(Number.isNaN(value) ? undefined : value)}
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {tabIndex == 2 && (
                  <div>
                    {plenumLanguages.length === 0 ? (
                      <div className="rounded-md border border-gray-300 bg-gray-100 p-3 text-sm text-gray-700">
                        No plenum languages available yet. Configure Languages first in the top section.
                      </div>
                    ) : (
                      <>
                        <div className="mb-4 flex flex-wrap gap-2 border-b border-gray-300 pb-2">
                          {plenumLanguages.map((language, index) => (
                            <button
                              key={`${language.Id ?? index}-${language.Language ?? ""}`}
                              type="button"
                              className={`rounded-md border px-3 py-1 text-sm ${language.Id === selectedFrameLanguageId ? "!border-blue-500 !bg-blue-50 !text-blue-700" : "border-gray-300 bg-white text-gray-700"}`}
                              onClick={() => setSelectedFrameLanguage(language.Id ?? 0)}
                            >
                              {language.Language}
                            </button>
                          ))}
                        </div>

                        <div className=" inline-flex  gap-4 w-full">
                          <button
                            type="button"
                            className={` px-3 py-1 text-sm ${frameContentMode === "primary" ? "!bg-blue-500 !rounded-b-none !text-white" : "text-gray-700 !border-none"}`}
                            onClick={() => setFrameContentMode("primary")}
                          >
                            Primary Content
                          </button>
                          <button
                            type="button"
                            className={` px-3 py-1 text-sm ${frameContentMode === "secondary" ? "!bg-blue-500 !rounded-b-none !text-white" : "text-gray-700 !border-none"}`}
                            onClick={() => setFrameContentMode("secondary")}
                          >
                            Secondary Content
                          </button>

                          <FormItem className="w-[12rem]  ml-4">
                            <FormLabel className="inline mr-4">Position</FormLabel>
                            <FormControl >
                              <OptionSet
                                className="inline-flex flex-row gap-2"
                                name={`position-${activeFrameBasePath}`}
                                options={[
                                  { label: "Left", value: 1 },
                                  { label: "Right", value: 2 },
                                ]}
                                value={Number(getActiveFrameValue("PositionID") ?? 1)}
                                onChange={(value) => setActiveFrameValue("PositionID", Number(value))}
                              />
                            </FormControl>
                          </FormItem>

                        </div>

                        {activeFrameBasePath == null ? (
                          <div className="rounded-md border border-gray-300 bg-gray-100 p-3 text-sm text-gray-700">
                            Loading frame configuration for this language/content type.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-5 border border-blue-500 rounded p-4">

                            <FormItem>
                              <FormLabel>Source Type</FormLabel>
                              <FormControl>
                                <Dropdown
                                  source={connTypes}
                                  value={getActiveFrameValue("ConnTypeId") as any}
                                  getLabel={(x) => x.type_name}
                                  getValue={(x) => x.ConnTypeId}
                                  onChange={(value) => setActiveFrameValue("ConnTypeId", Number(value))}
                                  searchable={true}
                                />
                              </FormControl>
                            </FormItem>

                            <FormItem className="col-span-3">
                              <FormLabel>Source</FormLabel>
                              <FormControl >
                                <>
                                  <Input
                                    value={String(getActiveFrameValue("Source") ?? "")}
                                    onChange={(e) => setActiveFrameValue("Source", e.target.value)}
                                    list={`source-list-${activeFrameBasePath}`}
                                  />
                                  <datalist id={`source-list-${activeFrameBasePath}`}>
                                    {getFilteredFrameSources(getActiveFrameValue("ConnTypeId")).map((x, i) => (
                                      <option key={`${x.Id ?? x.id ?? i}`} value={String(x.name ?? x.Name ?? "")} />
                                    ))}
                                  </datalist>
                                </>
                              </FormControl>
                            </FormItem>

                            <FormItem className="pt-6">
                              <FormControl>
                                <Checkbox
                                  label="All In One"
                                  checked={!!getActiveFrameValue("AllInOne")}
                                  onCheckedChange={(checked) => setActiveFrameValue("AllInOne", checked)}
                                />
                              </FormControl>
                            </FormItem>

                            <FormItem>
                              <FormLabel>FallBack Source Type</FormLabel>
                              <FormControl>
                                <Dropdown
                                  source={connTypes}
                                  value={getActiveFrameValue("FB_ConnTypeId") as any}
                                  getLabel={(x) => x.type_name}
                                  getValue={(x) => x.ConnTypeId}
                                  onChange={(value) => setActiveFrameValue("FB_ConnTypeId", Number(value))}
                                  searchable={true}
                                />
                              </FormControl>
                            </FormItem>

                            <FormItem className="col-span-3">
                              <FormLabel>FallBack Source</FormLabel>
                              <FormControl>
                                <>
                                  <Input
                                    value={String(getActiveFrameValue("FB_Source") ?? "")}
                                    onChange={(e) => setActiveFrameValue("FB_Source", e.target.value)}
                                    list={`fb-source-list-${activeFrameBasePath}`}
                                  />
                                  <datalist id={`fb-source-list-${activeFrameBasePath}`}>
                                    {getFilteredFrameSources(getActiveFrameValue("FB_ConnTypeId")).map((x, i) => (
                                      <option key={`${x.Id ?? x.id ?? i}`} value={String(x.name ?? x.Name ?? "")} />
                                    ))}
                                  </datalist>
                                </>
                              </FormControl>
                            </FormItem>

                            <FormItem className="pt-6">
                              <FormControl>
                                <Checkbox
                                  label="FB All In One"
                                  checked={!!getActiveFrameValue("FB_AllInOne")}
                                  onCheckedChange={(checked) => setActiveFrameValue("FB_AllInOne", checked)}
                                />
                              </FormControl>
                            </FormItem>
                            {getActiveFrameValue("FB_ConnTypeId") > 0 && (
                              <FormItem className="pt-1">
                                <FormControl>
                                  <Checkbox
                                    label="Allow User to switch to Fallback"
                                    checked={!!getActiveFrameValue("FB_UserToggleAllow")}
                                    onCheckedChange={(checked) => setActiveFrameValue("FB_UserToggleAllow", checked)}
                                  />
                                </FormControl>
                              </FormItem>

                            )}

                            {getActiveFrameValue("FB_ConnTypeId") > 0 && (
                              <FormItem className="pt-1 col-span-4">
                                <FormControl>
                                  <Checkbox
                                    label="Turn on Auto FB"
                                    checked={!!getActiveFrameValue("FB_UserToggleAuto")}
                                    onCheckedChange={(checked) => setActiveFrameValue("FB_UserToggleAuto", checked)}
                                  />
                                </FormControl>
                              </FormItem>

                            )}

                            <FormItem className="pt-6 ">
                              <FormLabel>Director Source Type</FormLabel>
                              <FormControl>
                                <Dropdown
                                  source={connTypes}
                                  value={getActiveFrameValue("Director_ConnTypeId") as any}
                                  getLabel={(x) => x.type_name}
                                  getValue={(x) => x.ConnTypeId}
                                  onChange={(value) => setActiveFrameValue("Director_ConnTypeId", Number(value))}
                                  searchable={true}
                                />
                              </FormControl>
                            </FormItem>

                            <FormItem className="col-span-3 pt-6 ">
                              <FormLabel>Director Source</FormLabel>
                              <FormControl>
                                <>
                                  <Input
                                    value={String(getActiveFrameValue("Director_Source") ?? "")}
                                    onChange={(e) => setActiveFrameValue("Director_Source", e.target.value)}
                                    list={`director-source-list-${activeFrameBasePath}`}
                                  />
                                  <datalist id={`director-source-list-${activeFrameBasePath}`}>
                                    {getFilteredFrameSources(getActiveFrameValue("Director_ConnTypeId")).map((x, i) => (
                                      <option key={`${x.Id ?? x.id ?? i}`} value={String(x.name ?? x.Name ?? "")} />
                                    ))}
                                  </datalist>
                                </>
                              </FormControl>
                            </FormItem>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                {tabIndex == 3 && (<div>


                  <div className="flex gap-3">
                    <FormField
                      control={form.control}
                      name="QandA.Active"
                      render={({ field }) => (
                        <FormItem className="pt-6">
                          <FormControl>
                            <Toggle
                              title="Q & A"
                              checked={!!field.value}
                              onChange={(e) => { field.onChange(e.target.checked); form.setValue("QandA.Live", e.target.checked); }}
                              name={field.name}
                              onBlur={field.onBlur}
                              ref={field.ref}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
      
                  </div>
                  {form.watch("QandA.Active") && (
                    <div>
                      <div className="flex items-center space-x-2 mt-3">


                        <FormField
                          control={form.control}
                          name="QandA.Title"
                          render={({ field }) => (
                            <FormItem className="flex-2">
                              <FormLabel>Title</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="QandA.Description"
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormLabel>Description</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="flex gap-3">
                        <FormField
                          control={form.control}
                          name="QandA.AutoApprove"
                          render={({ field }) => (
                            <FormItem >
                              <FormControl>
                                <Checkbox
                                  label="Automatically approve all Messages"
                                  checked={!!field.value}
                                  onCheckedChange={field.onChange}
                                  name={field.name}
                                  onBlur={field.onBlur}
                                  ref={field.ref}
                                  className="mt-6"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="QandA.AllowUpvote"
                          render={({ field }) => (
                            <FormItem >
                              <FormControl>
                                <Checkbox
                                  label="Allow Upvoting"
                                  checked={!!field.value}
                                  onCheckedChange={field.onChange}
                                  name={field.name}
                                  onBlur={field.onBlur}
                                  ref={field.ref}
                                  className="mt-6"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="QandA.IsAnonymous"
                          render={({ field }) => (
                            <FormItem >
                              <FormControl>
                                <Checkbox
                                  label="Make EVERYONE anonymous"
                                  checked={!!field.value}
                                  onCheckedChange={field.onChange}
                                  name={field.name}
                                  onBlur={field.onBlur}
                                  ref={field.ref}
                                  className="mt-6"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                      {!form.watch("QandA.IsAnonymous") && (
                        <div>
                          <div className="w-full mt-4">If anonymous login is enabled for event:</div>
                          <div className="flex gap-3">
                            <FormField
                              control={form.control}
                              name="QandA.NameRequired"
                              render={({ field }) => (
                                <FormItem >
                                  <FormControl>
                                    <Checkbox
                                      label="Name Required"
                                      checked={!!field.value}
                                      onCheckedChange={field.onChange}
                                      name={field.name}
                                      onBlur={field.onBlur}
                                      ref={field.ref}
                                      className="mt-1"
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="QandA.MediaRequired"
                              render={({ field }) => (
                                <FormItem >
                                  <FormControl>
                                    <Checkbox
                                      label="Media Required"
                                      checked={!!field.value}
                                      onCheckedChange={field.onChange}
                                      name={field.name}
                                      onBlur={field.onBlur}
                                      ref={field.ref}
                                      className="mt-1"
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          </div> </div>)}
                    </div>
                  )}
                </div>)}
{tabIndex == 4 && (

 <div className="overflow-x-auto">
         <table className="min-w-full text-sm">
           <thead className="bg-slate-100 text-left">
             <tr>
               <th className="px-3 py-2 w-[90px]">Sort</th>
               <th className="px-3 py-2">Id</th>
               <th className="px-3 py-2">Title</th>
               <th className="px-3 py-2">Type</th>
               <th className="px-3 py-2">Pointer Events</th>
             </tr>
           </thead>
           <tbody>
             { presentations.length === 0 ? (
               <tr className="border-t">
                 <td colSpan={8} className="px-3 py-4 text-center text-gray-500">
                   No presentations yet.
                 </td>
               </tr>
             ) : (
               presentations.map((item, index) => (
                 <tr
                   key={`${item.Id ?? "presentation"}-${index}`}
                   className={`border-t align-middle ${dragOverPresentationIndex === index ? "bg-slate-100" : ""}`}
                   onClick={() => {
                     if (draggedPresentationIndex != null) return;
                     setPresentationId(item.Id ?? null);
                   }}
                   onDragOver={(e) => {
                     e.preventDefault();
                     setDragOverPresentationIndex(index);
                   }}
                   onDrop={(e) => {
                     e.preventDefault();
                     e.stopPropagation();
                     handlePresentationDrop(index);
                     setDraggedPresentationIndex(null);
                     setDragOverPresentationIndex(null);
                   }}
                   onDragLeave={() => {
                     if (dragOverPresentationIndex === index) setDragOverPresentationIndex(null);
                   }}
                   style={{ cursor: "pointer" }}
                 >
                   <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      draggable
                      className="inline-flex cursor-grab items-center text-slate-500 hover:text-slate-700"
                      onClick={(e) => e.stopPropagation()}
                      onDragStart={(e) => {
                        e.stopPropagation();
                        setDraggedPresentationIndex(index);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => {
                        setDraggedPresentationIndex(null);
                        setDragOverPresentationIndex(null);
                      }}
                      title="Drag to reorder"
                      aria-label="Drag to reorder"
                    >
                      <GripVertical className="h-4 w-4" />
                    </button>
                   </td>

                   <td className="px-3 py-2">{item.Id ?? "-"}</td>
                   <td className="px-3 py-2">{item.Title ?? ""}</td>
                   <td className="px-3 py-2">{item.Type}</td>
                   <td className="px-3 py-2">{item.PointerEvents}</td>
                     <td><Trash2 onClick={(e) => { e.stopPropagation(); DeletePresentation(item.Id); }} /></td>
                   
                 </tr>
               ))
             )}
           </tbody>
         </table>
         <Button type='button' className="mt-4" onClick={() => setPresentationId(-1)}>Upload Presentation</Button>
       </div>
)}

{tabIndex == 5 && (

 <div className="overflow-x-auto">
         <table className="min-w-full text-sm">
           <thead className="bg-slate-100 text-left">
             <tr>
               <th className="px-3 py-2 w-[90px]">Live</th>
               <th className="px-3 py-2">Title</th>
               <th className="px-3 py-2">Actions</th>
             </tr>
           </thead>
           <tbody>
             { surveys.length === 0 ? (
               <tr className="border-t">
                 <td colSpan={8} className="px-3 py-4 text-center text-gray-500">
                   No surveys yet.
                 </td>
               </tr>
             ) : (
               surveys.map((item, index) => (
                 <tr
                   key={`${item.Id ?? "survey"}-${index}`}
                   className={`border-t align-middle}`}
                   
                 >
                   <td className="px-3 py-2 " onClick={(e) => e.stopPropagation()}>
                    <Toggle checked={!!(item.Live ?? false)} onChange={()=>toggleSurveyLive(item.Id ?? null)} />
                  </td>

                   <td className="px-3 py-2 w-[100%] cursor-pointer" onClick={(e)=>{ e.stopPropagation(); setSurveyId(item.Id)}}>{item.Name ?? ""}</td>
                     <td><div className="flex gap-x-2">
                      <Button type='button' onClick={(e) => {e.stopPropagation(); window.open(`${API_BASE_URL}/admin/SurveyExport?eventId=${eventId}&report=true&source=${item.Id}`)}}>
                        Results
                        </Button>
                        <Button type='button' onClick={() => setShowSurveyReset(item.Id??null)}>Reset</Button>
                        <Trash2 className="cursor-pointer" onClick={(e) => { e.stopPropagation(); DeleteSurvey(item.Id); }} /></div></td>
                   
                 </tr>
               ))
             )}
           </tbody>
         </table>
         <Button type='button' className="mt-4" onClick={() => setSurveyId(-1)}>New Survey</Button>
       </div>
)}
              </div>





            </div>


          </div>









          <div className="grid gap-2 grid-cols-7">
            {(currentPlenumId > 0 && tabIndex === 0) ? (
              <div className="flex col-span-3 cursor-pointer"  onClick={() => prepareDelete(true)}><Trash2 className="mr-2 " type="button" />Delete Plenum</div>
            ) : (
              <div className="col-span-3"></div>
            )}
            <Button className="w-full col-span-2" type="button" onClick={() => { console.log("Cancel clicked"); onClose(); }}>{translate("common.cancel")}</Button>

            <Button className="w-full col-span-2" type="submit">{translate("common.save")}</Button>
          </div>

          {(errorText != "") && (
            <div className="text-red-500 mt-2">
              {errorText}
            </div>
          )
          }




          <Popup
            isOpen={isDelete}
            onClose={() => prepareDelete(false)}
            title="Delete Plenum"
            className="w-[500px]"
          >
            <div>
              <div>Are you sure you want to delete this plenum?</div>
              <div className="grid gap-2 grid-cols-2 mt-4">
                <Button className="w-full" type="button" onClick={() => prepareDelete(false)}>Cancel</Button>
                <Button className="w-full" type="button" variant="destructive" onClick={() => { prepareDelete(false); deletePlenum(); }}>Delete</Button>


              </div>
            </div>
          </Popup>




          <Popup
            isOpen={showSurveyReset != null}
            onClose={() => setShowSurveyReset(null)}
            title="Reset Survey"
            className="w-[500px]"
          >
            <div>
              <div>Are you sure you want to delete answers for this survey?</div>
              <div className="grid gap-2 grid-cols-2 mt-4">
                <Button className="w-full" type="button" onClick={() => setShowSurveyReset(null)}>Cancel</Button>
                <Button className="w-full" type="button" variant="destructive" onClick={() => { setShowSurveyReset(null); deleteSurveyAnswers(showSurveyReset as number); }}>Delete</Button>


              </div>
            </div>
          </Popup>






          <Popup
            isOpen={presentationId != null}
            onClose={() => setPresentationId(null)}
            title="Presentation"
            className="w-[95%]"
          >
            <PresentationInfo Id={presentationId} plenumId={currentPlenumId} langs={plenumLanguages.map(l => l.Language as string)} eventId={eventId} 
            onClose={(refresh:boolean) => {setPresentationId(null); refresh && loadPresentations();}}
            />
          </Popup>


          <Popup
            isOpen={surveyId != undefined}
            onClose={() => setSurveyId(undefined)}
            title="Survey"
            className="w-[95%]"
          >
            <SurveyInfo Id={surveyId ?? null} plenumId={currentPlenumId} eventId={eventId} onClose={(refresh:boolean) => {setSurveyId(undefined); refresh && loadSurveys();}} />
          </Popup>




        </form>
      </Form>
    </div>

  )
}



